import type { Page, RecordValue, Unit } from "./types";

export type NextEventAction = {
  title: [string, string]; tab: string; read?: string; write?: string;
};

/** Recommendations only open existing views/forms; they never perform or authorize a transition. */
export function nextEventAction(unit: Unit | null, readiness?: RecordValue | null, live?: RecordValue | null): NextEventAction | null {
  if (!unit || unit.status === "ARCHIVED") return null;
  const check: NextEventAction = { title: ["فحص الجاهزية", "Check readiness"], tab: "structure", read: "readiness" };
  const checked = readiness?.checkedVersion === unit.version ? readiness : null;
  if (["COMPETITION", "STAGE", "CONTAINER"].includes(unit.unitKind)) return unit.contentMode === "ENTRIES"
    ? { title: ["مراجعة المشاركين", "Review participants"], tab: "entries", read: "entries" } : null;
  if (unit.unitKind === "RESULT") return { title: ["مراجعة إعداد النتيجة", "Review result setup"], tab: "structure", read: "resultPolicy" };
  const currentLive = live?.roundId === unit.unitId && typeof live.version === "number" && Number.isSafeInteger(live.version) && live.version >= unit.version ? live : null;
  const state = currentLive?.state || unit.roundState;
  const transition = (ar: string, en: string): NextEventAction => ({ title: [ar, en], tab: "structure", write: "roundLifecycle" });
  switch (state) {
    case "DRAFT": {
      if (checked?.ready === true && Array.isArray(checked.issues) && checked.issues.length === 0)
        return transition("تجهيز الجولة", "Prepare round");
      const issue = Array.isArray(checked?.issues) ? String(checked.issues[0] || "") : "";
      if (issue.startsWith("VOTING_POLICY")) return { title: ["إكمال قواعد التصويت", "Complete voting rules"], tab: "structure", write: "votingPolicy" };
      if (issue.startsWith("FORM")) return { title: ["إكمال نموذج التقييم", "Complete evaluation form"], tab: "structure", write: "evaluationForm" };
      if (issue.startsWith("SCHEDULE")) return { title: ["تحديد موعد التصويت", "Set voting schedule"], tab: "structure", write: "roundSchedule" };
      if (issue.startsWith("ENTR")) return { title: ["مراجعة المشاركين", "Review participants"], tab: "entries", read: "entries" };
      return check;
    }
    case "READY": {
      if (unit.status !== "PUBLISHED") return unit.status === "DRAFT"
        ? { title: ["مراجعة نشر الجولة", "Review round publication"], tab: "structure", write: "unitLifecycle" } : check;
      const schedule = currentLive?.schedule;
      if (schedule && typeof schedule === "object" && !Array.isArray(schedule) && schedule.mode === "MANUAL")
        return transition("فتح التصويت", "Open voting");
      return { title: ["مراجعة موعد بدء التصويت", "Review voting start"], tab: "structure", read: "roundLive" };
    }
    case "OPEN": return transition("مراجعة إغلاق التصويت", "Review closing voting");
    case "CLOSED": return transition("ختم التصويت", "Seal voting");
    case "SEALED": return { title: ["مراجعة النتائج", "Review results"], tab: "structure", read: "resultSnapshots" };
    default: return { title: ["مراجعة حالة الجولة", "Review round status"], tab: "structure", read: "roundLive" };
  }
}

export function collectionState(page: Page | null, filter: string, status: string, cursor = "") {
  if (!page) return "loading";
  if (page.items.length > 0) return "populated";
  if (filter || status) return "no-match";
  if (cursor || page.nextCursor) return "page-empty";
  return "empty";
}

export type CollectionResponse = { queryKey: string; page: Page };

/** A previous query cannot establish emptiness for a newly selected filter or scope. */
export function currentCollectionPage(response: CollectionResponse | null, queryKey: string): Page | null {
  return response?.queryKey === queryKey ? response.page : null;
}
